import React, { useState, useEffect, useRef, useMemo } from "react";
import { bool, number, func, array } from "prop-types";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { Trans, useTranslation } from "@lib/i18n";
import { useRouter } from "next/router";
import { twMerge } from "tailwind-merge";

import Radio from "../shared/Radio";
import Typography from "../shared/Typography";
import CollapsableContainer from "@components/shared/CollapsableContainer";
import StickyRequestButton from "@components/shared/StickyRequestButton";
import CitroenRegistration from "./CitroenRegistration";
import ConfirmChoiceModal from "./ConfirmChoiceModal";

import { useRoutingUtils } from "@hooks/useRoutingUtils";
import { ROUTES } from "@shared/routing/routes";
import { financeSimulationType } from "@types";
import { getIsCashJourneySelected } from "@shared/v2/lib/getIsCashJourneySelected";
import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";
import { getIsVehicleRegistrationByCitroenSelected } from "@shared/v2/lib/getIsVehicleRegistrationByCitroenSelected";
import { getIsVehicleRegistrationByCustomerSelected } from "@shared/v2/lib/getIsVehicleRegistrationByCustomerSelected";
import {
  useCEESubsidySelector,
  useCurrencySettingsSelector,
} from "@redux/reducers/appConfig";
import {
  useCurrentDealSelector,
  useDealSelector,
  useIsCEESubsidySelectedSelector,
} from "@redux/reducers/deal";
import { useGetJourneySpecificConfig } from "@hooks/useGetJourneySpecificConfig";
import { getIsLeasingJourneySelected } from "@shared/v2/lib/getIsLeasingJourneySelected";

import { postDealAction } from "@redux/actions/dealActions";

import {
  CTA_SOL_TYPES,
  DEAL_TYPE,
  DEAL_STATES,
  VEHICLE_REGISTRATION_METHOD,
  BUSINESS_MODELS,
} from "@shared/constants";
import CarteGriseDetail from "./CarteGriseDetail";
import ConfirmCEESubsidyModal from "./ConfirmCEESubsidyModal";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { getCarteGriseAmount } from "@shared/v2/lib/getCarteGriseAmount";
import { getFinanceSimulationCarteGrise } from "@shared/v2/lib/getDealCarteGrise";
import { toast } from "@lib/reactToastify";
import { isRegistrationCostValid } from "@lib/isRegistrationCostValid";

const VehicleRegistration = ({
  isSummary,
  availableMethods,
  disabled,
  index,
  isOpen,
  financeSimulation,
  openVehicleRegistration,
  closeVehicleRegistration,
  onVehicleRegistrationSelected,
  onVehicleRegistrationConfirmed,
  updateVehicleRegistration,
  initialGTMSent,
  fixedHeaderHeight,
  openRegistrationCostChangedModal,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const router = useRouter();
  const routingUtils = useRoutingUtils();
  const currency = useCurrencySettingsSelector();
  const currentDeal = useCurrentDealSelector();
  const isB2BSelected = useIsB2BSelected();

  const ceeSubsidySelected = useIsCEESubsidySelectedSelector();
  const ceeSubsidy = useCEESubsidySelector(ceeSubsidySelected);

  const isDefaultRegistrationSelected = Boolean(
    currentDeal?.financeSimulation?.vehicleRegistration?.type
  );

  const journeyConfiguration = useGetJourneySpecificConfig();

  const dealCarteGrise = getFinanceSimulationCarteGrise(financeSimulation);
  const carteGrisePrice = getCarteGriseAmount(financeSimulation, true, false);

  const deal = useDealSelector();
  const {
    userProfile: { registrationPostalCode },
  } = deal;

  const isCarteGriseVisible = dealCarteGrise?.enabled || !!carteGrisePrice;

  const { vehicleRegistration, journey } = financeSimulation;
  const { CITROEN, MYSELF } = VEHICLE_REGISTRATION_METHOD;

  const [isChoiceConfirmModalOpen, setIsChoiceConfirmModalOpen] = useState(
    false
  );
  const [
    isCEESubsidyConfirmModalOpen,
    setIsCEESubsidyConfirmModalOpen,
  ] = useState(false);
  const isSelfRegistrationConfirmed = useRef(false);

  const isCashJourneySelected = getIsCashJourneySelected(journey);

  const isLeasingJourneySelected = getIsLeasingJourneySelected(journey);

  const isSelfRegistrationEnabled = availableMethods?.some(
    method => method.method === MYSELF
  );

  const isCitroenRegistrationEnabled = availableMethods?.some(
    method => method.method === CITROEN
  );

  const selfRegistrationPrice =
    journeyConfiguration?.vehicleRegistration?.selfRegistration?.price;

  const showChoiceConfirmationModal =
    isCitroenRegistrationEnabled &&
    isSelfRegistrationEnabled &&
    isCashJourneySelected &&
    getIsVehicleRegistrationByCustomerSelected(vehicleRegistration);

  const showCEESubsidyConfirmationModal =
    !isB2BSelected && ceeSubsidy?.enabled && !!ceeSubsidySelected;

  useEffect(() => {
    if (!initialGTMSent) return;

    if (!isSummary && availableMethods.length === 1 && !vehicleRegistration) {
      updateVehicleRegistration(availableMethods[0]);
    }
  }, [initialGTMSent]);

  const handleRegistrationChange = event => {
    isSelfRegistrationConfirmed.current = false;
    onVehicleRegistrationSelected(event);
  };

  const handleSelfRegistrationConfirm = async () => {
    isSelfRegistrationConfirmed.current = true;
    await triggerRequest();
    setIsChoiceConfirmModalOpen(false);
  };

  const triggerRequest = async () => {
    return dispatch(
      postDealAction(
        CTA_SOL_TYPES.OFFER_CREATED,
        DEAL_TYPE.DEAL,
        DEAL_STATES.OFFER
      )
    );
  };

  const onContinueButtonClick = async () => {
    await onVehicleRegistrationConfirmed();

    if (isRegistrationByCitroen) {
      try {
        const isCarteGriseValid = await isRegistrationCostValid({
          journeyType: financeSimulation.journey,
          businessModel: isB2BSelected
            ? BUSINESS_MODELS.B2B
            : BUSINESS_MODELS.B2C,
          dealCarteGrise,
          registrationPostalCode,
        });

        if (!isCarteGriseValid) {
          openRegistrationCostChangedModal();
          return false;
        }
      } catch (error) {
        toast.error(t("notify.apiDefaultError"));
        return false;
      }
    }

    if (
      !isSelfRegistrationConfirmed.current &&
      showChoiceConfirmationModal &&
      !isChoiceConfirmModalOpen
    ) {
      setIsChoiceConfirmModalOpen(true);
      return false;
    }

    if (!isSelfRegistrationConfirmed.current) {
      await triggerRequest();
    }

    if (showCEESubsidyConfirmationModal) {
      setIsCEESubsidyConfirmModalOpen(true);
      return false;
    }

    await onRequestSuccess();

    return new Promise(() => null);
  };

  const onRequestSuccess = async () => {
    await router.push(
      ROUTES.OFFER_SUMMARY,
      routingUtils.getLocalizedPathname(
        ROUTES.OFFER_SUMMARY,
        null,
        i18n.language
      )
    );

    return new Promise(() => null);
  };

  const isRegistrationByCitroen = getIsVehicleRegistrationByCitroenSelected(
    vehicleRegistration
  );
  const isRegistrationByCustomer = getIsVehicleRegistrationByCustomerSelected(
    vehicleRegistration
  );

  const summaryText = useMemo(() => {
    if (isCarteGriseVisible) {
      return t(
        `basket.vehicleRegistration.carteGrise.${
          isCashJourneySelected ? "cash" : "finance"
        }`
      );
    }

    if (isCashJourneySelected) {
      return t(
        `basket.vehicleRegistration.${
          isRegistrationByCitroen ? "optionCitroen" : "optionByMyself"
        }.button`
      );
    }

    if (isLeasingJourneySelected) {
      return t(`basket.vehicleRegistration.financeDescriptionPart1`);
    }
  }, [isRegistrationByCitroen, isCarteGriseVisible]);

  return (
    <CollapsableContainer
      isDisabled={disabled}
      isOpen={isOpen}
      onChange={isOpen =>
        isOpen ? closeVehicleRegistration?.() : openVehicleRegistration?.()
      }
      title={t("basket.vehicleRegistration.title")}
      titleIndex={index}
    >
      {isSummary ? (
        <>
          <Typography className="font-semibold my-4">{summaryText}</Typography>
          {isRegistrationByCitroen && !!isCarteGriseVisible && (
            <CarteGriseDetail
              financeSimulation={financeSimulation}
              readOnly={true}
            />
          )}
        </>
      ) : (
        <>
          {isSelfRegistrationEnabled && isCitroenRegistrationEnabled ? (
            <>
              <Typography className="text-sm">
                <Trans i18nKey="basket.vehicleRegistration.description" />
              </Typography>
              <div
                className={twMerge(
                  "mt-4 mb-2 rounded-md px-2 py-4 border transition duration-300",
                  isRegistrationByCitroen
                    ? "bg-white border-greyLight"
                    : "border-greyLight/50 hover:bg-white/80"
                )}
              >
                <div className="flex justify-between">
                  <Radio
                    checked={isRegistrationByCitroen}
                    label={t("basket.vehicleRegistration.optionCitroen.button")}
                    id={CITROEN}
                    onChange={handleRegistrationChange}
                    dataId="details-pers-new-client-radio"
                  />
                  {isCarteGriseVisible && (
                    <Typography className="font-semibold whitespace-nowrap">
                      +
                      {getFormattedPrice(
                        carteGrisePrice,
                        currency.format,
                        currency.symbol
                      )}
                    </Typography>
                  )}
                </div>
                <CitroenRegistration
                  financeSimulation={financeSimulation}
                  registrationType={vehicleRegistration}
                />
              </div>
              <div
                className={twMerge(
                  "mt-4 mb-2 rounded-md px-2 py-4 border transition duration-300",
                  isRegistrationByCustomer
                    ? "bg-white border-greyLight"
                    : "border-greyLight/50 hover:bg-white/80"
                )}
              >
                <Radio
                  checked={isRegistrationByCustomer}
                  label={t("basket.vehicleRegistration.optionByMyself.button")}
                  id={MYSELF}
                  onChange={handleRegistrationChange}
                  dataId="details-pers-existing-client-radio"
                />
                <motion.div
                  initial="collapsed"
                  animate={
                    getIsVehicleRegistrationByCustomerSelected(
                      vehicleRegistration
                    )
                      ? "open"
                      : "collapsed"
                  }
                  exit="collapsed"
                  variants={{
                    open: {
                      opacity: 1,
                      height: "auto",
                      display: "block",
                    },
                    collapsed: {
                      opacity: 0,
                      height: 0,
                      transitionEnd: { display: "none" },
                    },
                  }}
                >
                  <div className="pl-10 py-2">
                    <Typography
                      data-id="basket-summary-configuration-heading"
                      className="text-sm"
                    >
                      {t(
                        "basket.vehicleRegistration.optionByMyself.description",
                        {
                          price: getFormattedPrice(
                            selfRegistrationPrice,
                            currency.format,
                            currency.symbol
                          ),
                        }
                      )}
                    </Typography>
                  </div>
                </motion.div>
              </div>
            </>
          ) : (
            <>
              {isCitroenRegistrationEnabled && (
                <>
                  <Typography className="text-sm">
                    <Trans
                      i18nKey="basket.vehicleRegistration.optionCitroen.description"
                      values={{
                        price: getFormattedPrice(
                          selfRegistrationPrice,
                          currency.format,
                          currency.symbol
                        ),
                      }}
                    />
                  </Typography>
                  {!!isCarteGriseVisible && (
                    <CarteGriseDetail financeSimulation={financeSimulation} />
                  )}
                </>
              )}
              {isSelfRegistrationEnabled && (
                <Typography className="text-sm">
                  <Trans
                    i18nKey="basket.vehicleRegistration.optionByMyself.description"
                    values={{
                      price: getFormattedPrice(
                        selfRegistrationPrice,
                        currency.format,
                        currency.symbol
                      ),
                    }}
                  />
                </Typography>
              )}
            </>
          )}

          <div className="flex justify-center pt-4 pb-1 w-full">
            <StickyRequestButton
              data-id="basket-registration-vehicle-continue-button"
              disabled={
                (isSelfRegistrationEnabled &&
                  !vehicleRegistration &&
                  !isDefaultRegistrationSelected) ||
                disabled
              }
              fixedHeaderHeight={fixedHeaderHeight}
              isStickyToBottom={true}
              isStickyToTop={true}
              onRequestError={() => {}}
              triggerRequestCallback={onContinueButtonClick}
            >
              {t("general.continue")}
            </StickyRequestButton>
          </div>

          {showChoiceConfirmationModal && (
            <ConfirmChoiceModal
              triggerRequestCallback={handleSelfRegistrationConfirm}
              onRequestSuccess={onContinueButtonClick}
              rejectHandle={() => setIsChoiceConfirmModalOpen(false)}
              isOpen={isChoiceConfirmModalOpen}
            />
          )}
          {showCEESubsidyConfirmationModal && (
            <ConfirmCEESubsidyModal
              onRequestSuccess={onRequestSuccess}
              isOpen={isCEESubsidyConfirmModalOpen}
              ceeSubsidyType={ceeSubsidySelected}
            />
          )}
        </>
      )}
    </CollapsableContainer>
  );
};

VehicleRegistration.propTypes = {
  index: number.isRequired,
  isSummary: bool,
  disabled: bool,
  isOpen: bool,
  openVehicleRegistration: func,
  closeVehicleRegistration: func,
  financeSimulation: financeSimulationType,
  availableMethods: array.isRequired,
  onVehicleRegistrationSelected: func.isRequired,
  onVehicleRegistrationConfirmed: func.isRequired,
  updateVehicleRegistration: func,
  initialGTMSent: bool,
  fixedHeaderHeight: number,
  openRegistrationCostChangedModal: func,
};

export default VehicleRegistration;
